import React, { useState } from "react";

import { useNavigate } from "react-router-dom";

import { NO_FOLDER_FILTER } from "src/api";
import { Flow } from "src/api/flowTypes";
import {
  useDeleteFlow,
  useEditFlow,
  useFlows,
  useWorkspace,
} from "src/api/queries";
import { toastActions } from "src/design-system/Toast/utils";
import { DeleteFlowModal } from "src/flowsOverview/DeleteFlowModal";
import { EditFlowModal } from "src/flowsOverview/EditFlowModal";
import { FlowListSkeletonV2 } from "src/flowsOverview/v2/FlowListSkeletonV2";
import { FlowListV2 } from "src/flowsOverview/v2/FlowListV2";
import {
  useCreateFolder,
  useFolders,
} from "src/flowsOverview/v2/folderQueries";
import { MoveFlowModal } from "src/flowsOverview/v2/MoveFlowModal";
import { FolderDropdownSelection } from "src/flowsOverview/v2/SelectFolderDropdown";
import { useOrderFlowsBy } from "src/flowsOverview/v2/useOrderFlowsBy";
import { useSubmitFlowSettingsUpdate } from "src/flowsOverview/v2/useSubmitFlowSettingsUpdate";
import { useCapabilities } from "src/hooks/useCapabilities";
import { flowFolderScope } from "src/hooks/useCapabilities/flowFolderScope";
import { useFolderIdSearchParam } from "src/router/SearchParams";
import {
  getBaseUrl,
  getFlowVersionsUrl,
  getLinkToApiDocsPage,
  getUrlToReviewQueue,
} from "src/router/urls";
import { copyTextToClipboard } from "src/utils/clipboard";
import { logger } from "src/utils/logger";
import { wrapWithAxiosResponseErrorHandler } from "src/utils/toastError";

type FlowFolderListProps = {
  organizationId: string;
  workspaceId: string;
};

export const FlowFolderList: React.FC<FlowFolderListProps> = ({
  organizationId,
  workspaceId,
}) => {
  const [searchParamFolderId] = useFolderIdSearchParam();
  const [orderFlowsBy, _setOrderFlowsBy] = useOrderFlowsBy();

  const flowResult = useFlows({
    workspaceId,
    folderId: searchParamFolderId || NO_FOLDER_FILTER,
    orderBy: orderFlowsBy,
  });
  const [folderId] = useFolderIdSearchParam();
  const foldersResult = useFolders({ workspaceId: workspaceId });
  const { flowVersions: flowVersionsPermissions } = useCapabilities();
  const workspace = useWorkspace(workspaceId);

  const [currentFlow, setCurrentFlow] = useState<Flow>();
  const [editModalIsOpen, setEditModalIsOpen] = useState<boolean>(false);
  const [moveModalIsOpen, setMoveModalIsOpen] = useState<boolean>(false);
  const [flowToDelete, setFlowToDelete] = useState<
    { id: string; slug: string } | undefined
  >();
  const [deleteConfirmationModalIsOpen, setDeleteConfirmationModalIsOpen] =
    useState<boolean>(false);
  const deleteFlow = useDeleteFlow();
  const editFlow = useEditFlow(workspaceId);
  const createFolder = useCreateFolder();

  const onSubmitFlowSettings = useSubmitFlowSettingsUpdate(
    workspaceId,
    currentFlow,
  );

  const navigate = useNavigate();

  const onViewFlow = (flow: Flow) => {
    if (flowVersionsPermissions.canAccess(flowFolderScope(flow))) {
      navigate(getFlowVersionsUrl(organizationId, workspaceId, flow.id));
    } else {
      navigate(getUrlToReviewQueue(organizationId, workspaceId, flow.id));
    }
  };

  const onRename = (flow: Flow) => {
    setCurrentFlow(flow);
    setEditModalIsOpen(true);
  };

  const onMove = (flow: Flow) => {
    setCurrentFlow(flow);
    setMoveModalIsOpen(true);
  };

  const onCopy = (flow: Flow) => {
    copyTextToClipboard(
      getBaseUrl() + getFlowVersionsUrl(organizationId, workspaceId, flow.id),
    ).then(() => {
      toastActions.success({
        title: "Link copied to clipboard",
      });
    });
  };

  const onDelete = (flow: Flow) => {
    setFlowToDelete({ id: flow.id, slug: flow.slug });
    setDeleteConfirmationModalIsOpen(true);
  };

  const onViewApiDocs = (flow: Flow) => {
    try {
      const apiDocsUrl = getLinkToApiDocsPage(
        organizationId,
        workspaceId,
        flow,
      );
      if (apiDocsUrl) {
        navigate(apiDocsUrl);
      }
    } catch {
      logger.error(`Couldnt access docs of flow ${flow.name}`);
    }
  };

  const onCloseModal = () => {
    setEditModalIsOpen(false);
  };

  const onConfirmMoveModal = async ({
    folderSelection,
  }: {
    folderSelection: FolderDropdownSelection;
  }) => {
    let folderId: string | null = null;
    if (folderSelection.mode === "addFolder") {
      const newFolder = (
        await createFolder.mutateAsync({
          name: folderSelection.folderName,
          workspace_id: workspaceId,
        })
      ).data;
      folderId = newFolder.id;
    } else {
      folderId = folderSelection.folderId;
    }
    if (currentFlow) {
      await editFlow.mutateAsync({
        flowId: currentFlow.id,
        flow_folder_id: folderId,
        old_folder_id: currentFlow.flow_folder_id,
      });
    }
  };

  const onRemoveFromFolder = wrapWithAxiosResponseErrorHandler(
    async (flow: Flow) => {
      await editFlow.mutateAsync({
        flowId: flow.id,
        flow_folder_id: null,
        old_folder_id: flow.flow_folder_id,
      });
    },
  );

  const onCloseDeleteModal = () => {
    setDeleteConfirmationModalIsOpen(false);
    setFlowToDelete(undefined);
  };

  const onConfirmDeleteModal = async () => {
    if (flowToDelete) {
      try {
        await deleteFlow.mutateAsync(flowToDelete.id);
        toastActions.success({ title: "Successfully deleted flow" });
      } catch {
        logger.error("Error deleting flow");
      }
    }
    setDeleteConfirmationModalIsOpen(false);
    setFlowToDelete(undefined);
  };

  const skeletonCount =
    folderId === undefined
      ? foldersResult.data?.flows_outside_folders_count
      : foldersResult.data?.folders.find((folder) => folder.id === folderId)
          ?.flow_count;

  return (
    <>
      <div className="min-h-0 flex-1">
        <div className="scrollbar-hide h-full min-h-0 overflow-auto bg-gray-100 px-4 py-5">
          {flowResult.isLoading ? (
            <FlowListSkeletonV2 skeletonCount={skeletonCount} />
          ) : (
            <FlowListV2
              align="start"
              flowActions={{
                onCopy,
                onDelete,
                onRename,
                onViewApiDocs,
                onViewFlow,
                onMove,
                onRemoveFromFolder,
              }}
              flows={flowResult.data}
            />
          )}
        </div>
        <EditFlowModal
          flow={currentFlow}
          mode="edit"
          open={editModalIsOpen}
          organizationId={organizationId}
          title="Edit Decision Flow"
          workspaceId={workspaceId}
          onClose={onCloseModal}
          onConfirm={onSubmitFlowSettings}
          onUnmount={() => setCurrentFlow(undefined)}
        />
        <DeleteFlowModal
          flowId={flowToDelete?.id}
          flowSlug={flowToDelete?.slug}
          isOpen={deleteConfirmationModalIsOpen}
          workspace={workspace.data}
          onClose={onCloseDeleteModal}
          onConfirm={onConfirmDeleteModal}
        />
        <MoveFlowModal
          flow={currentFlow}
          open={moveModalIsOpen}
          workspaceId={workspaceId}
          onAddFolder={() => {}}
          onClose={() => setMoveModalIsOpen(false)}
          onConfirm={onConfirmMoveModal}
        />
      </div>
    </>
  );
};
